import React, { forwardRef, useCallback, useRef } from 'react';
import RCSlider from 'rc-slider';
import {
  ROOT_CLASS,
  RANGE_CLASS,
  MIN_POSITION,
  MAX_POSITION,
  POSITION_STEP,
  RC_SLIDER_PREFIX_CLASS,
  RC_SLIDER_RAIL_SELECTOR,
  GRADIENT_STOPS_RAIL_BACKGROUND_CSS_VAR,
} from './constants';
import { calculatePosition } from './helpers/calculate-position';
import { isRailClickTarget } from './verifiers/is-rail-click-target';
import { Handle } from './components/gradient-stop-handle';
import useStopsSliderState from './hooks/use-stops-slider-state';

const { Range } = RCSlider;

function assignRef(targetRef, value) {
  if (typeof targetRef === 'function') {
    targetRef(value);
    return;
  }

  if (targetRef != null) {
    const objectRef = targetRef;
    objectRef.current = value;
  }
}

function getClientXFromRailEvent(event) {
  if ('clientX' in event) {
    return event.clientX;
  }

  const firstTouch = event.touches[0];
  const changedTouch = event.changedTouches[0];
  const touchPoint = firstTouch ?? changedTouch;

  return touchPoint?.clientX ?? null;
}

const GradientStopsSlider = forwardRef(
  ({ background, stops, activeStopId, onDragStart, onDrag, onDragEnd, onRailClick }, ref) => {
    const rootRef = useRef(null);
    const {
      sortedStops,
      positions,
      handleCaptureStopId,
      handleInteractionStart,
      handleInteraction,
      handleInteractionEnd,
    } = useStopsSliderState({
      stops,
      onDragStart,
      onDrag,
      onDragEnd,
    });

    const setRootRefs = useCallback(
      (node) => {
        rootRef.current = node;
        assignRef(ref, node);
      },
      [ref]
    );

    const handleRailInteractionCapture = useCallback(
      (e) => {
        if (!onRailClick) {
          return;
        }

        if ('button' in e && e.button !== 0) {
          return;
        }

        const { target } = e;

        if (!(target instanceof HTMLElement) || !isRailClickTarget(target)) {
          return;
        }

        const clientX = getClientXFromRailEvent(e);

        if (clientX == null) {
          return;
        }

        const element = rootRef.current;

        if (!element) {
          return;
        }

        const railElement = element.querySelector(RC_SLIDER_RAIL_SELECTOR);
        const rect = railElement?.getBoundingClientRect() ?? element.getBoundingClientRect();
        const position = calculatePosition(clientX, rect);

        e.stopPropagation();
        e.preventDefault();

        onRailClick({ position });
      },
      [onRailClick]
    );

    const handleRailMouseDownCapture = useCallback(
      (e) => {
        handleRailInteractionCapture(e);
      },
      [handleRailInteractionCapture]
    );

    const handleRailTouchStartCapture = useCallback(
      (e) => {
        handleRailInteractionCapture(e);
      },
      [handleRailInteractionCapture]
    );

    const renderHandle = useCallback(
      (handleProps) => {
        const stop = sortedStops[handleProps.index];

        return (
          <Handle
            key={stop.id}
            stop={stop}
            handleProps={handleProps}
            isActive={stop.id === activeStopId}
            onCapture={handleCaptureStopId}
          />
        );
      },
      [activeStopId, handleCaptureStopId, sortedStops]
    );

    return (
      <div
        ref={setRootRefs}
        className={ROOT_CLASS}
        style={{ [GRADIENT_STOPS_RAIL_BACKGROUND_CSS_VAR]: background }}
        onMouseDownCapture={handleRailMouseDownCapture}
        onTouchStartCapture={handleRailTouchStartCapture}
      >
        <Range
          min={MIN_POSITION}
          max={MAX_POSITION}
          step={POSITION_STEP}
          included={false}
          value={positions}
          handle={renderHandle}
          className={RANGE_CLASS}
          prefixCls={RC_SLIDER_PREFIX_CLASS}
          onBeforeChange={handleInteractionStart}
          onChange={handleInteraction}
          onAfterChange={handleInteractionEnd}
        />
      </div>
    );
  }
);

GradientStopsSlider.displayName = 'GradientStopsSlider';

export default GradientStopsSlider;
